import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../ui/use-toast';
import { boardDashboardService } from '../../../services/board-dashboard';
import { climbsService } from '../../../services/climbs';
import { devicesService } from '../../../services/devices';
import type {
  Board,
  BoardCalibration,
  BoardDisabledCells,
  BoardExtraHoldMap,
  BoardHand,
  BoardHoldMap,
  BoardHoldRadii,
  BoardHoldRole,
  BoardShapes,
} from '../../../types/device';
import type { BoardHold, Climb } from '../../../types/climb';
import { assignHands, clearHands, missingHoldRoles, orderHolds, startingHandOf } from './roles';
import { isScannedWall, isShapedWall, scannedWallOf, type ScannedWall } from './geometry';
import {
  clearDefaultInclination,
  getDefaultInclination,
  setDefaultInclination,
} from '../../../utils/board-inclination-preference';

/** The angles a board can be set to, in the 5° steps its jacks allow. */
export const INCLINATION_STEPS: number[] = Array.from({ length: 17 }, (_, index) => index * 5);

/**
 * How long the selection has to stay still before it is sent to the wall.
 * Painting a problem is a burst of taps, and the device relays every command
 * over BLE, so firing on each one would queue up lights the setter has already
 * moved past.
 */
const LIVE_PREVIEW_DEBOUNCE_MS = 400;

/** What the grid needs to be drawn, read off the board and its type. */
export interface BoardGrid {
  imageUrl: string | null;
  rows: number;
  columns: number;
  calibration: BoardCalibration | null;
  /** The wall's own holds — a mapped layout (Kilter) has them, a plain grid does not. */
  boardHolds: BoardHoldMap | null;
  /** The holds that are not on the grid, so a setter can put one in a problem. */
  extraHolds: BoardExtraHoldMap | null;
  /** The cells with nothing on them, so the grid does not offer them. */
  disabledCells: BoardDisabledCells | null;
  /**
   * The lines a drawn wall's holds are strung along. Any at all and they are
   * the wall — the grid is not drawn and `rows`/`columns` mean nothing.
   */
  shapes: BoardShapes | null;
  /**
   * A wall described by its photo's scan: its holds are the traced ones, and
   * they win over the grid and the lines (docs/SPRAY_WALL_SCAN_PLAN.md).
   */
  scanned: ScannedWall | null;
  holdRadii: BoardHoldRadii | null;
  /** Whether there is anything to paint on: a photo and a wall to put on it. */
  hasGrid: boolean;
}

export interface BoardClimbDraft {
  name: string;
  setName: (name: string) => void;
  gradeId: string;
  setGradeId: (id: string) => void;
  description: string;
  setDescription: (description: string) => void;
  /**
   * The selection as it stands, hands and all: what is painted on the board,
   * sent to the wall and saved. Setting it stores the roles and the order — the
   * hands are the mode's to work out, and are put back on every read.
   */
  holds: BoardHold[];
  setHolds: React.Dispatch<React.SetStateAction<BoardHold[]>>;
  circuit: boolean;
  /** Turns circuit mode on or off, giving the holds their order or taking the focus off the wall. */
  setCircuit: (on: boolean) => void;
  /** Forced hands: every hold also says which hand takes it. Circuits only. */
  forcedHands: boolean;
  setForcedHands: (on: boolean) => void;
  /** The hand the first hold is taken with — the setter's one say in the alternation. */
  startingHand: BoardHand;
  setStartingHand: (hand: BoardHand) => void;
  /**
   * Where in the circuit the hold lit on its own sits, or null for none. A
   * place in the run rather than a cell, since a circuit may use the same hold
   * more than once and the two uses are pointed at one without the other.
   */
  focusedHold: number | null;
  setFocusedHold: (index: number | null) => void;
  inclination: number;
  setInclination: (degrees: number) => void;
  /** The wheel's own change: picks the angle *and* keeps the board's default in step. */
  pickInclination: (degrees: number) => void;
  inclinationIsDefault: boolean;
  setInclinationIsDefault: (on: boolean) => void;
  /** Re-reads the stored default, for a wheel opening after one was dismissed. */
  syncInclinationDefault: () => void;
  livePreview: boolean;
  setLivePreview: React.Dispatch<React.SetStateAction<boolean>>;
  saving: boolean;
  /** Which of the three required roles the problem still has no hold for. */
  missingRoles: BoardHoldRole[];
  canSave: boolean;
  grid: BoardGrid;
  adjustableInclination: boolean;
  grades: NonNullable<NonNullable<Board['grading_system']>['grades']>;
  /** Creates the problem, toasting either way; null means it was not created. */
  save: () => Promise<Climb | null>;
  /**
   * Saves the changes to the problem being edited, toasting either way. False
   * means nothing was saved. The updated row is not handed back: the caller
   * asks the server for the list again, since a corrected problem may well have
   * moved under the filters and the sort that list is drawn with.
   */
  saveEdit: () => Promise<boolean>;
  /** Back to an empty problem on the same board. */
  reset: () => void;
}

/**
 * The problem an edit starts from. A subset of the dashboard's row rather than
 * the row itself, so the hook stays a board-grid thing and does not take the
 * whole panel's payload as its input.
 */
export interface EditedBoardClimb {
  id: string;
  name: string;
  description: string | null;
  gradeId: string | null;
  inclination: number | null;
  circuit: boolean;
  forcedHands: boolean;
  holds: BoardHold[];
}

/**
 * One board problem being drawn, shared by the two places a problem is put up:
 * the member form at `/gyms/:slug/boards/:id/climbs/new` and the board panel in
 * the gym admin.
 *
 * Everything that is the problem rather than the page lives here — the holds
 * and their order, the fields, what the save is still waiting for, mirroring
 * the selection onto the wall while it is drawn, and the create call itself.
 * The two surfaces then lay their own controls out around it: one is a phone
 * form the setter holds at the wall, the other a column of a desktop dashboard.
 */
export function useBoardClimbDraft({
  gymSlug,
  boardId,
  board,
  editing = null,
}: {
  gymSlug: string | undefined;
  /**
   * The board being set on, from the route rather than from `board` — the board
   * itself is fetched, and the angle it was last set at has to be on the wheel
   * from the first render rather than replacing whatever is on it later.
   */
  boardId: string | undefined;
  board: Board | null;
  /**
   * A problem already on the wall, to correct rather than to add. The draft
   * opens on it and `saveEdit` writes it back; leave it out to put a new
   * problem up.
   */
  editing?: EditedBoardClimb | null;
}): BoardClimbDraft {
  const { t } = useTranslation();
  const { toast } = useToast();

  // An edit opens on the problem it is correcting; a new problem opens empty.
  const [name, setName] = useState(editing?.name ?? '');
  const [gradeId, setGradeId] = useState(editing?.gradeId ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  // The roles and the order the setter painted. The hands are not kept here:
  // they follow from this and from the mode, so they are worked out on the way
  // out (see `holds`) rather than stored twice and left to drift.
  const [selection, setHolds] = useState<BoardHold[]>(editing?.holds ?? []);
  // Circuit mode: the holds stop being a set and become the order they are
  // climbed in, walked through one lit hold at a time.
  const [circuit, setCircuitState] = useState(editing?.circuit ?? false);
  // Forced hands: that order also says which hand takes each hold, alternating
  // from the one the circuit starts on.
  const [forcedHands, setForcedHandsState] = useState(editing?.forcedHands ?? false);
  const [startingHand, setStartingHand] = useState<BoardHand>(() =>
    startingHandOf(editing?.holds ?? [])
  );
  const [focusedHold, setFocusedHold] = useState<number | null>(null);
  // The angle this board's problems are being set at, and whether the setter
  // asked for it to be the one the next problem opens on. A problem being
  // corrected keeps the angle it was graded at, whatever the board's default is.
  const [inclination, setInclination] = useState(
    () => editing?.inclination ?? getDefaultInclination(boardId) ?? 30
  );
  const [inclinationIsDefault, setInclinationIsDefault] = useState(
    () => getDefaultInclination(boardId) !== null
  );
  const [livePreview, setLivePreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // The problem being edited, read through a ref rather than as a dependency:
  // it is rebuilt on every list refresh, and reseeding the draft on that would
  // throw away whatever the setter had just changed. Which problem it is — the
  // id — is the only thing that should reopen the form.
  const editingRef = useRef(editing);
  editingRef.current = editing;

  const editedId = editing?.id;
  useEffect(() => {
    const source = editingRef.current;
    if (!source) return;

    setName(source.name);
    setGradeId(source.gradeId ?? '');
    setDescription(source.description ?? '');
    setHolds(source.holds);
    setCircuitState(source.circuit);
    setForcedHandsState(source.forcedHands);
    setStartingHand(startingHandOf(source.holds));
    setFocusedHold(null);
    if (source.inclination !== null) setInclination(source.inclination);
  }, [editedId]);

  // The selection as everything outside this hook sees it. A forced circuit
  // gets its hands worked out here, once, so the board, the list, the wall and
  // the save can never disagree about them; anything else is stripped of hands
  // it may be carrying — a problem that stops forcing them keeps none.
  const holds = useMemo(
    () => (forcedHands ? assignHands(selection, startingHand) : clearHands(selection)),
    [selection, forcedHands, startingHand]
  );

  const grid = useMemo<BoardGrid>(() => {
    const imageUrl = board?.image_url ?? board?.board_type.image_url ?? null;
    const rows = board?.rows ?? board?.board_type.rows ?? 0;
    const columns = board?.columns ?? board?.board_type.columns ?? 0;
    const scanned = scannedWallOf(board?.scanned_holds, board?.image_crop, board?.hidden_scanned_holds);

    return {
      imageUrl,
      rows,
      columns,
      calibration: board?.calibration ?? board?.board_type.calibration ?? null,
      boardHolds: board?.holds ?? null,
      extraHolds: board?.extra_holds ?? null,
      disabledCells: board?.disabled_cells ?? null,
      shapes: board?.shapes ?? null,
      scanned,
      holdRadii: board?.hold_radii ?? null,
      // A wall drawn as lines has its holds on them, so there is something to
      // paint on whether or not anybody ever filled in a grid for it.
      // So does one described by its scan, whose holds are the traced ones.
      hasGrid: !!imageUrl && (isScannedWall(scanned) || isShapedWall(board?.shapes) || (rows > 0 && columns > 0)),
    };
  }, [board]);

  const grades = useMemo(() => board?.grading_system?.grades ?? [], [board]);
  const adjustableInclination = board?.adjustable_inclination ?? false;

  const setCircuit = useCallback((on: boolean) => {
    setCircuitState(on);
    // Holds picked as a set have no order; becoming a circuit gives them one,
    // and a circuit is climbed from its start holds to its finish holds.
    if (on) return setHolds(orderHolds);

    // Leaving circuit mode leaves the board fully lit again — and takes the
    // hands with it: they alternate down a sequence, and a set of holds has
    // none. The server drops them the same way, so a form that kept the switch
    // on would be promising something the save would not keep.
    setFocusedHold(null);
    setForcedHandsState(false);
  }, []);

  const setForcedHands = useCallback((on: boolean) => {
    setForcedHandsState(on);
    // Hands are read off the climbed order, so switching them on settles the
    // order first — the same thing circuit mode does when it is switched on
    // over a selection painted as a set.
    if (on) setHolds(orderHolds);
  }, []);

  const pickInclination = useCallback(
    (degrees: number) => {
      setInclination(degrees);
      // The switch decides with the wheel: whatever angle is confirmed is the
      // one remembered, and turning it off forgets the board's default rather
      // than leaving a stale one behind.
      if (inclinationIsDefault) setDefaultInclination(boardId, degrees);
      else clearDefaultInclination(boardId);
    },
    [boardId, inclinationIsDefault]
  );

  // A switch flipped and then dismissed has decided nothing, so the wheel opens
  // on what was actually saved.
  const syncInclinationDefault = useCallback(() => {
    setInclinationIsDefault(getDefaultInclination(boardId) !== null);
  }, [boardId]);

  // Mirrors the selection onto the wall while it is being drawn, so the setter
  // reads the problem off the board instead of off the photo. Best-effort, like
  // the climb profile's light button: the command leaving says nothing about
  // what the LEDs actually did.
  useEffect(() => {
    if (!livePreview || !gymSlug || !boardId || holds.length === 0) return;

    const timer = setTimeout(() => {
      // Drawing a circuit is a run of commands to the same board — hold the
      // Bluetooth link so each one lights at once instead of reconnecting. It is
      // per command, so it rides on every one of them.
      const options = circuit ? { keepAlive: true } : {};
      devicesService.previewBoardLights(gymSlug, boardId, holds, options).catch((error: unknown) => {
        // Switch off rather than toast again on the next tap: a board that
        // refused one selection will refuse the ones after it too.
        setLivePreview(false);
        toast({
          title: t('climbs.board.lightError'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      });
    }, LIVE_PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [livePreview, holds, circuit, gymSlug, boardId, toast, t]);

  // A problem is not one until it says where it begins, what it is climbed on
  // and where it tops out — so the three roles gate the save, and the form says
  // which of them is still missing rather than greying the button out silently.
  // Only where there is a grid to paint on: a board with no photo has no holds
  // to give, and blocking it would leave a form nothing could get out of.
  const missingRoles = useMemo(
    () => (grid.hasGrid ? missingHoldRoles(holds) : []),
    [grid.hasGrid, holds]
  );

  const canSave = useMemo(
    () => name.trim().length > 1 && gradeId.length > 0 && missingRoles.length === 0 && !saving,
    [name, gradeId, missingRoles, saving]
  );

  const reset = useCallback(() => {
    setName('');
    setGradeId('');
    setDescription('');
    setHolds([]);
    setCircuitState(false);
    setForcedHandsState(false);
    setStartingHand('left');
    setFocusedHold(null);
  }, []);

  const save = useCallback(async (): Promise<Climb | null> => {
    if (!board || !canSave) return null;

    setSaving(true);
    try {
      const created = await climbsService.createClimb({
        type: 'board_climb',
        name: name.trim(),
        grade_id: gradeId,
        ...(board.global ? { board_type_id: board.board_type.id } : { board_id: board.id }),
        ...(adjustableInclination ? { inclination } : {}),
        description: description.trim() || undefined,
        // A circuit's holds are sent in the order the list puts them in — that
        // order is the circuit, and the hands each hold carries follow from it.
        circuit,
        forced_hands: forcedHands,
        holds,
      });
      toast({ title: t('climbs.board.created') });
      return created;
    } catch (error) {
      toast({
        title: t('climbs.board.createError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      return null;
    } finally {
      setSaving(false);
    }
  }, [
    board,
    canSave,
    name,
    gradeId,
    adjustableInclination,
    inclination,
    description,
    circuit,
    forcedHands,
    holds,
    toast,
    t,
  ]);

  const saveEdit = useCallback(async (): Promise<boolean> => {
    if (!gymSlug || !boardId || !editedId || !canSave) return false;

    setSaving(true);
    try {
      await boardDashboardService.updateClimb(gymSlug, boardId, editedId, {
        name: name.trim(),
        grade_id: gradeId,
        description: description.trim() || null,
        circuit,
        forced_hands: forcedHands,
        ...(adjustableInclination ? { inclination } : {}),
        // The selection travels whether or not it was touched: an edit that
        // leaves the holds alone sends back what it opened on, which is the
        // same thing the server already holds.
        holds,
      });
      toast({ title: t('climbs.board.updated') });
      return true;
    } catch (error) {
      toast({
        title: t('climbs.board.updateError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    gymSlug,
    boardId,
    editedId,
    canSave,
    name,
    gradeId,
    description,
    circuit,
    forcedHands,
    adjustableInclination,
    inclination,
    holds,
    toast,
    t,
  ]);

  return {
    name,
    setName,
    gradeId,
    setGradeId,
    description,
    setDescription,
    holds,
    setHolds,
    circuit,
    setCircuit,
    forcedHands,
    setForcedHands,
    startingHand,
    setStartingHand,
    focusedHold,
    setFocusedHold,
    inclination,
    setInclination,
    pickInclination,
    inclinationIsDefault,
    setInclinationIsDefault,
    syncInclinationDefault,
    livePreview,
    setLivePreview,
    saving,
    missingRoles,
    canSave,
    grid,
    adjustableInclination,
    grades,
    save,
    saveEdit,
    reset,
  };
}
